import * as React from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PillSearchField from "../../components/PillSearchField";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { registerTherapist, deleteTherapist, updateTherapist, fetchTherapists } from "../../Services/apiService";

function Therapist({ embedded = false }) {
  // State for therapists data
  const [therapists, setTherapists] = React.useState([]);
  const [filteredTherapists, setFilteredTherapists] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [operationLoading, setOperationLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  
  // Pagination state  
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 30,
  });
  const previousPageRef = React.useRef(0); // Track previous page to detect page changes

  // UI state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedTherapist, setSelectedTherapist] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [addTherapistDialogOpen, setAddTherapistDialogOpen] = React.useState(false);
  const [updateTherapistDialogOpen, setUpdateTherapistDialogOpen] = React.useState(false);

  // Form data for add therapist
  const [formData, setFormData] = React.useState({
    therapistName: "",
    practiceName: "",
    city: "",
    email: "",
  });

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Incremental fetch for therapists
  React.useEffect(() => {
    let isMounted = true;

    const fetchTherapistsIncrementally = async () => {
      try {
        setLoading(true);
        setError(null);

        let page = 1;
        const pageSize = 120;
        let allFetchedTherapists = [];

        while (true) {
          console.log(`📡 Fetching therapists page ${page}...`);
          const response = await fetchTherapists(page, pageSize);

          if (!response || !response.therapists || response.therapists.length === 0) {
            console.log("✅ No more therapists to fetch.");
            break;
          }

          const transformedData = response.therapists.map(therapist => ({
            id: therapist.therapist_id,
            therapistName: therapist.therapist_name,
            practiceName: therapist.practice_name,
            city: therapist.city,
            email: therapist.therapist_email,
            isRegistered: therapist.is_registered,
            registeredRoles: therapist.registered_roles || [],
          }));

          allFetchedTherapists = [...allFetchedTherapists, ...transformedData];

          if (response.therapists.length < pageSize) {
            break;
          }

          page += 1;
        }

        if (isMounted) {
          setTherapists(allFetchedTherapists);
          setFilteredTherapists(allFetchedTherapists);
        }
      } catch (error) {
        console.error("Error fetching therapists:", error);
        if (isMounted) {
          setError(error.message || "Failed to load therapists");
          setSnackbar({
            open: true,
            message: "Failed to load therapists",
            severity: "error",
          });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTherapistsIncrementally();

    return () => {
      isMounted = false;
    };
  }, []);

  // Helper function to refresh therapists list incrementally
const refreshTherapists = async () => {
  try {
    setError(null);
    setLoading(true);

    let page = 1;
    const pageSize = 120;
    let allFetchedTherapists = [];

    while (true) {
      console.log(`📡 Refreshing therapists page ${page}...`);
      const response = await fetchTherapists(page, pageSize);

      if (!response || !response.therapists || response.therapists.length === 0) {
        console.log("✅ No more therapists to fetch.");
        break;
      }

      const transformedData = response.therapists.map(therapist => ({
        id: therapist.therapist_id,
        therapistName: therapist.therapist_name,
        practiceName: therapist.practice_name,
        city: therapist.city,
        email: therapist.therapist_email,
        isRegistered: therapist.is_registered,
        registeredRoles: therapist.registered_roles || [],
      }));

      allFetchedTherapists = [...allFetchedTherapists, ...transformedData];

      // Progressive display: update table after each batch
      setTherapists([...allFetchedTherapists]);
      setFilteredTherapists([...allFetchedTherapists]);

      if (response.therapists.length < pageSize) {
        break;
      }

      page += 1;
    }
  } catch (error) {
    console.error("Failed to refresh therapists:", error);
    setError(error.message || "Failed to refresh therapists");
    setSnackbar({
      open: true,
      message: "Failed to refresh therapists",
      severity: "error",
    });
  } finally {
    setLoading(false);
  }
};

  // Filter therapists based on search query (name or email)
  React.useEffect(() => {
    let filtered = [...therapists];

    // Search filter (therapist name or email) - real-time
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((therapist) => 
        therapist.therapistName?.toLowerCase().includes(query) ||
        therapist.email?.toLowerCase().includes(query)
      );
    }

    setFilteredTherapists(filtered);
  }, [therapists, searchQuery]);

  // Clear search filter on any page change 
  React.useEffect(() => {
    // If page changed from the previous page, clear search filter
    if (previousPageRef.current !== paginationModel.page) {
      setSearchQuery("");
      // Update the previous page reference
      previousPageRef.current = paginationModel.page;
    }
  }, [paginationModel.page]);

  const handleMenuClick = (event, therapist) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTherapist(therapist);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTherapist(null);
  };

  const handleAddTherapist = () => {
    setAddTherapistDialogOpen(true);
  };

  const handleDeleteTherapist = () => {
    setDeleteDialogOpen(true);
    // Don't call handleMenuClose() here - keep selectedTherapist for the delete operation
    setAnchorEl(null); // Just close the menu
  };

  const handleUpdateTherapist = () => {
    if (selectedTherapist) {
      // Pre-fill form with selected therapist data
      setFormData({
        therapistName: selectedTherapist.therapistName,
        practiceName: selectedTherapist.practiceName,
        city: selectedTherapist.city,
        email: selectedTherapist.email,
      });
      setUpdateTherapistDialogOpen(true);
      // Don't call handleMenuClose() here - keep selectedTherapist for the update operation
      setAnchorEl(null); // Just close the menu
    }
  };

  const confirmDeleteTherapist = async () => {
    if (operationLoading) return; // Prevent multiple clicks
    
    setOperationLoading(true);
    try {
      if (!selectedTherapist?.id) {
        setSnackbar({
          open: true,
          message: "No therapist selected for deletion.",
          severity: "error",
        });
        return;
      }

      console.log("Deleting therapist:", selectedTherapist);
      
      // Delete therapist using apiService
      const result = await deleteTherapist(selectedTherapist.id);
      console.log("Delete API Response:", result);

      await refreshTherapists();
      // Show success message
      setSnackbar({
        open: true,
        message: `Therapist ${selectedTherapist.therapistName} deleted successfully!`,
        severity: "success",
      });
      

      setDeleteDialogOpen(false);
      setSelectedTherapist(null);
    } catch (error) {
      console.error("Error deleting therapist:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete therapist",
        severity: "error",
      });
    } finally {
      setOperationLoading(false);
    }
  };

  const confirmAddTherapist = async () => {
    if (operationLoading) return; // Prevent multiple clicks
    
    const { therapistName, practiceName, city, email } = formData;
    try {
      setOperationLoading(true);
      
      // Prepare the JSON data to send (matching API format)
      const therapistData = {
        therapist_name: therapistName || "",
        practice_name: practiceName || "",
        city: city || "",
        therapist_email: email || ""
      };

      console.log("Sending therapist data:", therapistData);

      // Register therapist using apiService
      const result = await registerTherapist(therapistData);
      console.log("API Response:", result);

      await refreshTherapists();
      // Show success message
      setSnackbar({
        open: true,
        message: "Therapist registered successfully!",
        severity: "success",
      });
      
      setAddTherapistDialogOpen(false);
      setFormData({ therapistName: "", practiceName: "", city: "", email: "" });
    } catch (error) {
      console.error("Error adding therapist:", error);
      setSnackbar({
        open: true,
        message: "Failed to register therapist",
        severity: "error",
      });
    } finally {
      setOperationLoading(false);
    }
  };

  const cancelDeleteTherapist = () => {
    if (operationLoading) return; // Prevent closing during loading
    setDeleteDialogOpen(false);
    setSelectedTherapist(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const cancelAddTherapist = () => {
    setAddTherapistDialogOpen(false);
    setFormData({ therapistName: "", practiceName: "", city: "", email: "" });
  };

  const confirmUpdateTherapist = async () => {
    if (operationLoading) return; // Prevent multiple clicks
    
    // Check if we have a selected therapist
    if (!selectedTherapist?.id) {
      setSnackbar({
        open: true,
        message: "No therapist selected for update.",
        severity: "error",
      });
      return;
    }
    
    const { therapistName, practiceName, city, email } = formData;
    try {
      setOperationLoading(true);
      
      // Prepare the JSON data to send (matching API format)
      const therapistData = {
        therapistName: therapistName || "",
        practiceName: practiceName || "",
        city: city || "",
        email: email || ""
      };

      console.log("Updating therapist with ID:", selectedTherapist.id);
      console.log("Sending update data:", therapistData);

      // Update therapist using apiService
      const result = await updateTherapist(selectedTherapist.id, therapistData);
      console.log("Update API Response:", result);

      await refreshTherapists();
      // Show success message
      setSnackbar({
        open: true,
        message: "Therapist updated successfully!",
        severity: "success",
      });
      
      // Update the specific therapist in local state instead of full reload
      const updatedTherapist = {
        id: selectedTherapist.id,
        therapistName: therapistName || "",
        practiceName: practiceName || "",
        city: city || "",
        email: email || ""
      };
      
      setTherapists(prevTherapists => 
        prevTherapists.map(therapist => 
          therapist.id === selectedTherapist.id ? updatedTherapist : therapist
        )
      );
      setFilteredTherapists(prevFiltered => 
        prevFiltered.map(therapist => 
          therapist.id === selectedTherapist.id ? updatedTherapist : therapist
        )
      );
      
      setUpdateTherapistDialogOpen(false);
      setSelectedTherapist(null);
      // Reset form data
      setFormData({ therapistName: "", practiceName: "", city: "", email: "" });
    } catch (error) {
      console.error("Error updating therapist:", error);
      setSnackbar({
        open: true,
        message: "Failed to update therapist",
        severity: "error",
      });
    } finally {
      setOperationLoading(false);
    }
  };

  const cancelUpdateTherapist = () => {
    setUpdateTherapistDialogOpen(false);
    setFormData({ therapistName: "", practiceName: "", city: "", email: "" });
    setSelectedTherapist(null);
  };

  // Columns definition
  const columns = [
    {
      field: "therapistName",
      headerName: "Therapist Name",
      minWidth: 160,
      flex: 1.5,
    },
    {
      field: "practiceName",
      headerName: "Practice Name",
      minWidth: 140,
      flex: 1.5,
    },
    {
      field: "city",
      headerName: "City",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 180,
      flex: 2,
    },
    {
      field: "isRegistered",
      headerName: "Registration",
      minWidth: 220,
      flex: 1.8,
      sortable: true,
      valueGetter: (value, row) => (row.isRegistered ? "Registered" : "Not Registered"),
      renderCell: (params) => {
        const { isRegistered, registeredRoles } = params.row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              height: "100%",
              flexWrap: "nowrap",
              overflow: "hidden",
            }}
          >
            <Chip
              label={isRegistered ? "Registered" : "Not Registered"}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.72rem",
                bgcolor: isRegistered ? "#DCFCE7" : "#F3F4F6",
                color: isRegistered ? "#15803D" : "#6B7280",
                border: isRegistered ? "1px solid #86EFAC" : "1px solid #E5E7EB",
              }}
            />
            {isRegistered &&
              registeredRoles?.map((role) => (
                <Chip
                  key={role}
                  label={role.charAt(0).toUpperCase() + role.slice(1)}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: "0.68rem",
                    borderColor: "#163666",
                    color: "#163666",
                  }}
                />
              ))}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      flex: 0,
      renderCell: (params) => (
        <IconButton
          onClick={(event) => handleMenuClick(event, params.row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {/* Top Row */}
      {!embedded && (
        <Box sx={{ px: 1, py: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
          <LocalHospitalIcon sx={{ fontSize: "1.5rem", color: "#333" }} />
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Therapists
          </Typography>
        </Box>
      )}

      {embedded && (
        <Typography sx={{ px: 1, pt: 0.5, pb: 1.5, fontSize: "1.125rem", fontWeight: 600, color: "#111827" }}>
          Therapists
        </Typography>
      )}

      {/* Search + actions row */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "nowrap",
          mx: embedded ? 0 : 1,
          mb: 2,
          p: 1.5,
          backgroundColor: "#FFFFFF",
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: embedded ? 200 : 300 }}>
          <PillSearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Enter therapist name or email"
            resultCount={filteredTherapists.length}
            loading={loading}
          />
        </Box>
        <Button
          variant="outlined"
          onClick={handleAddTherapist}
          disabled={loading}
          sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#163666", color: "#163666", px: 1.25, ml: "auto", flexShrink: 0, whiteSpace: "nowrap", "&:hover": { borderColor: "#112B51", backgroundColor: "rgba(22,54,102,0.06)" } }}
        >
          Add Therapist
        </Button>
      </Box>

      {/* Data Table */}
      <Box sx={{ mx: embedded ? 0 : 1, mb: 2, backgroundColor: "#FFFFFF", border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400
          }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{
            p: 3,
            textAlign: 'center',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Error loading therapists
            </Typography>
            <Typography color="error" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Box>
        ) : (
          <DataGrid
            rows={filteredTherapists}
            columns={columns}
            autoHeight
            rowHeight={40}
            columnHeaderHeight={40}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[30, 50, 100]}
            disableRowSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            sx={{
              border: "none",
              borderRadius: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleUpdateTherapist}>
          Update Therapist
        </MenuItem>
        <MenuItem 
          onClick={handleDeleteTherapist} 
          sx={{ color: "error.main" }}
        >
          Delete Therapist
        </MenuItem>
      </Menu>

      {/* Delete Therapist Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={operationLoading ? undefined : cancelDeleteTherapist}
        aria-labelledby="delete-therapist-dialog-title"
        aria-describedby="delete-therapist-dialog-description"
      >
        <DialogTitle id="delete-therapist-dialog-title">Delete Therapist</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-therapist-dialog-description">
            Are you sure you want to permanently delete{" "}
            <strong>{selectedTherapist?.therapistName}</strong>? This action cannot be
            undone and will remove all associated data.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={cancelDeleteTherapist} variant="outlined" color="primary" disabled={operationLoading}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteTherapist}
            color="error"
            variant="contained"
            autoFocus
            disabled={operationLoading}
          >
            {operationLoading ? "Deleting..." : "Delete Therapist"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Therapist Dialog */}
      <Dialog
        open={addTherapistDialogOpen}
        onClose={operationLoading ? undefined : cancelAddTherapist}
        aria-labelledby="add-therapist-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="add-therapist-dialog-title">Add New Therapist</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Therapist Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter therapist name"
              variant="outlined"
              value={formData.therapistName}
              onChange={(e) => handleFormChange("therapistName", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              Practice Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter practice name"
              variant="outlined"
              value={formData.practiceName}
              onChange={(e) => handleFormChange("practiceName", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              City
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter city"
              variant="outlined"
              value={formData.city}
              onChange={(e) => handleFormChange("city", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter email address"
              variant="outlined"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button 
            onClick={cancelAddTherapist} 
            variant="outlined" 
            color="primary"
            disabled={operationLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmAddTherapist}
            variant="contained"
            autoFocus
            disabled={!formData.therapistName || !formData.city || !formData.email || operationLoading}
            sx={{ bgcolor: "#163666", "&:hover": { bgcolor: "#112B51" } }}
          >
            {operationLoading ? "Adding Therapist..." : "Add Therapist"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Therapist Dialog */}
      <Dialog
        open={updateTherapistDialogOpen}
        onClose={operationLoading ? undefined : cancelUpdateTherapist}
        aria-labelledby="update-therapist-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="update-therapist-dialog-title">Update Therapist</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Therapist Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter therapist name"
              variant="outlined"
              value={formData.therapistName}
              onChange={(e) => handleFormChange("therapistName", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              Practice Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter practice name"
              variant="outlined"
              value={formData.practiceName}
              onChange={(e) => handleFormChange("practiceName", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              City
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter city"
              variant="outlined"
              value={formData.city}
              onChange={(e) => handleFormChange("city", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter email address"
              variant="outlined"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button 
            onClick={cancelUpdateTherapist} 
            variant="outlined" 
            color="primary"
            disabled={operationLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmUpdateTherapist} 
            color="primary" 
            variant="contained" 
            autoFocus
            disabled={!formData.therapistName || !formData.city || !formData.email || operationLoading}
          >
            {operationLoading ? "Updating Therapist..." : "Update Therapist"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Therapist;
